import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Copy,
  Check,
  Link2,
  Share2,
  Instagram,
  Twitter,
  MessageCircle,
  Mail,
  Send,
  MessageSquare,
  Sparkles,
} from "lucide-react";

/**
 * XEA TikTok mark — lucide doesn't ship one. Tiny SVG so the bar feels native.
 */
const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.5 7.5a6 6 0 0 1-3.5-1.1V15a5 5 0 1 1-5-5v3a2 2 0 1 0 2 2V2h3a3 3 0 0 0 3 3v2.5Z" />
  </svg>
);

const absoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const buildShareText = (caption = "", productUrl = "") => {
  let s = (caption || "").trim();
  if (productUrl) s += `\n\n🔗 ${productUrl}`;
  return s;
};

/**
 * One-tap share bar for any generated creative or video.
 *
 *  • Instagram & TikTok are intentionally rendered as **Coming Soon** because
 *    XEA does not yet have approved Graph / Open API access. Clicking them
 *    shows a friendly toast explaining the status — keeps the feature visible
 *    in the product so users notice it the moment it ships.
 *  • X, WhatsApp, Email, native Web Share, and Copy Link work today.
 *
 * Props:
 *   mediaUrl    — the image/video URL to share
 *   caption     — text caption (used for tweet/share intent)
 *   hashtags    — array of hashtags (appended to caption when sharing)
 *   productUrl  — optional URL to append (deep link to the store)
 *   variant     — "full" (default) | "compact" (icons only)
 *   className   — wrapper class override
 */
export function SocialShareBar({
  mediaUrl,
  caption = "",
  hashtags = [],
  productUrl = "",
  variant = "full",
  className = "",
}) {
  const { token } = useAuth();
  const [copied, setCopied] = useState(false);
  const [waitlist, setWaitlist] = useState({ instagram: false, tiktok: false });

  // Load which waitlists this user is already on (one-time)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/waitlist/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const set = new Set(r.data?.platforms || []);
        setWaitlist({ instagram: set.has("instagram"), tiktok: set.has("tiktok") });
      } catch (e) {
        // non-fatal — just keeps Notify-me buttons enabled
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const joinWaitlist = async (platform) => {
    if (!token) {
      toast.error("Log in to join the waitlist.");
      return;
    }
    if (waitlist[platform]) {
      toast.success(`You're already on the ${platform} waitlist.`);
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/waitlist/join`,
        { platform },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWaitlist((w) => ({ ...w, [platform]: true }));
      toast.success(
        `You're on the ${platform === "instagram" ? "Instagram" : "TikTok"} waitlist. We'll email you the moment it launches.`,
        { duration: 5000 }
      );
    } catch (e) {
      toast.error("Couldn't add you to the waitlist. Try again in a moment.");
    }
  };

  const fullUrl = absoluteUrl(mediaUrl);
  const tagString = (hashtags || [])
    .map((t) => (t.startsWith("#") ? t : `#${t}`))
    .join(" ");
  const shareText = buildShareText(
    [caption, tagString].filter(Boolean).join("\n\n"),
    productUrl
  );

  // --- Working actions -----------------------------------------------------

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Link copied — paste it anywhere.");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Couldn't copy. Try the download button instead.");
    }
  };

  const handleX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText
    )}&url=${encodeURIComponent(fullUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(
      `${shareText}\n${fullUrl}`
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEmail = () => {
    const subject = encodeURIComponent("Check out this XEA creative");
    const body = encodeURIComponent(`${shareText}\n\n${fullUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(
      fullUrl
    )}&text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSMS = () => {
    // iOS prefers `sms:&body=`, Android prefers `sms:?body=`. Browsers accept both
    // but iOS treats `?` as a no-op; we use the `&` form which works on both.
    const body = encodeURIComponent(`${shareText}\n${fullUrl}`);
    window.location.href = `sms:&body=${body}`;
  };

  // Native Web Share — on MOBILE we try to attach the actual file (so the user
  // shares a real video / image, not just a link). If that fails (desktop,
  // CORS, unsupported MIME), we fall back to sharing the URL.
  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopyLink();
      return;
    }

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

    // Mobile path → try file share first
    if (isMobile && navigator.canShare) {
      try {
        const r = await fetch(fullUrl, { credentials: "omit" });
        if (r.ok) {
          const blob = await r.blob();
          const ext = (blob.type.split("/")[1] || "bin").split("+")[0];
          const file = new File([blob], `xea-creative.${ext}`, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: "XEA creative", text: shareText });
            return;
          }
        }
      } catch (_) {
        // fall through to link share
      }
    }

    // Desktop or fallback → link share
    try {
      await navigator.share({
        title: "XEA creative",
        text: shareText,
        url: fullUrl,
      });
    } catch (e) {
      // user cancelled — no-op
    }
  };

  // --- Disabled-with-explanation actions -----------------------------------

  const handleComingSoon = (platform) => () => {
    const platformKey = platform.toLowerCase();
    const alreadyOnList = waitlist[platformKey];
    toast.custom((id) => (
      <div className="flex items-start gap-3 rounded-2xl bg-white border border-ink-200/70 shadow-elevated p-4 max-w-md">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-coral-500/15 text-coral-500">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <div className="text-sm leading-snug flex-1">
          <p className="font-semibold text-ink-950">
            {platform} auto-post is coming soon
          </p>
          <p className="text-ink-500 mb-2">
            We&apos;re waiting on {platform}&apos;s API approval. Until then, tap{" "}
            <span className="font-semibold text-ink-700">Download</span> or{" "}
            <span className="font-semibold text-ink-700">Copy</span> and post manually in 10 seconds.
          </p>
          {alreadyOnList ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] text-coral-500">
              <Check className="h-3 w-3" /> You&apos;re on the waitlist
            </span>
          ) : (
            <button
              type="button"
              onClick={() => { joinWaitlist(platformKey); toast.dismiss(id); }}
              className="text-[11px] font-bold uppercase tracking-[0.18em] text-coral-500 hover:text-ink-950 underline-offset-4 hover:underline cursor-pointer"
              data-testid={`notify-me-${platformKey}`}
            >
              🔔 Notify me when it launches
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => toast.dismiss(id)}
          className="text-ink-300 hover:text-ink-950 shrink-0 text-lg leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>
    ), { duration: 8000 });
  };

  // --- Buttons -------------------------------------------------------------

  const buttons = [
    {
      key: "ig",
      label: "Instagram",
      icon: Instagram,
      onClick: handleComingSoon("Instagram"),
      disabled: true,
      tone: "instagram",
      joined: waitlist.instagram,
    },
    {
      key: "tt",
      label: "TikTok",
      icon: TikTokIcon,
      onClick: handleComingSoon("TikTok"),
      disabled: true,
      tone: "tiktok",
      joined: waitlist.tiktok,
    },
    { key: "x", label: "X", icon: Twitter, onClick: handleX },
    { key: "wa", label: "WhatsApp", icon: MessageCircle, onClick: handleWhatsApp },
    { key: "tg", label: "Telegram", icon: Send, onClick: handleTelegram },
    { key: "sms", label: "SMS", icon: MessageSquare, onClick: handleSMS },
    { key: "mail", label: "Email", icon: Mail, onClick: handleEmail },
    { key: "share", label: "Share", icon: Share2, onClick: handleNativeShare },
    {
      key: "copy",
      label: copied ? "Copied" : "Copy link",
      icon: copied ? Check : Link2,
      onClick: handleCopyLink,
      highlight: copied,
    },
  ];

  if (variant === "compact") {
    return (
      <div
        className={`flex flex-wrap items-center gap-2 ${className}`}
        data-testid="social-share-bar-compact"
      >
        {buttons.map((b) => {
          // Strip `key` out of the spread — React 19 warns about
          // a `key` ending up inside spread props.
          const { key: _k, ...rest } = b;
          return <ShareIcon key={b.key} {...rest} />;
        })}
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-ink-200/70 bg-white p-4 ${className}`}
      data-testid="social-share-bar"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
          Share to social
        </p>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-coral-500">
          <span className="h-1.5 w-1.5 rounded-full bg-coral-500 animate-pulse" />
          IG / TikTok — auto-post coming soon
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {buttons.map((b) => {
          const { key: _k, ...rest } = b;
          return <ShareIcon key={b.key} {...rest} />;
        })}
      </div>
    </div>
  );
}

// Single share button — rendered as a pill with icon + label, animated hover.
function ShareIcon({ label, icon: Icon, onClick, disabled, tone, highlight, joined }) {
  const base =
    "relative inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold transition-all duration-200 will-change-transform " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-950";
  let cls = "";
  if (disabled) {
    cls =
      "bg-ink-100 text-ink-500 hover:bg-ink-200/80 hover:text-ink-700 cursor-help";
  } else if (highlight) {
    cls = "bg-ink-950 text-white";
  } else {
    cls =
      "bg-ink-50 text-ink-950 hover:bg-ink-950 hover:text-white hover:-translate-y-0.5";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${cls}`}
      data-testid={`share-${label.toLowerCase().replace(/\s+/g, "-")}`}
      data-disabled={disabled || undefined}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
      {disabled && (
        <AnimatePresence>
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`ml-1 inline-flex h-4 items-center rounded-full px-1.5 text-[9px] font-bold uppercase tracking-[0.15em] ${
              joined ? "bg-coral-500/20 text-coral-500" : "bg-coral-500 text-white"
            }`}
          >
            {joined ? "✓" : "Soon"}
          </motion.span>
        </AnimatePresence>
      )}
    </button>
  );
}

export default SocialShareBar;
